import React, { ReactNode, useEffect, useRef } from 'react';
import { useBoolean, useStateful, useNumber } from 'react-hanger';
import { observer } from 'mobx-react';

import isPlainObject from 'lodash/isPlainObject';
import assign from 'lodash/assign';
import pickBy from 'lodash/pickBy';
import theme, { WithTheme } from 'lib/theme';
import hoc from 'lib/hoc';
import { hooks, messenger, track } from 'lib';
import { globalStore, useGlobalStore } from 'stores';
import { getUrl } from 'lib/base';
import { Button } from 'client/widgets/ui';
import Icon from '../icon';
import { Wrap, Mask, Header, Body, Footer } from './styled';

let zIndex = 8001; // theme.zIndex.modal

const ModalButton = (props) => {
  const { key, outline, ...restProps } = props;
  if (window.inPcClient) {
    const { className, ...rest } = props;
    return <Button round {...rest}></Button>;
  }
  return <button {...restProps}></button>;
};

const getZIndex = () => {
  zIndex += 2;
  return zIndex;
};

type IProps = {
  id?: string;
  title?: any;
  closeEnable?: boolean;
  cancelEnable?: boolean;
  maskCloseEnable?: boolean;
  isHeaderNoBorder?: boolean;
  isShowFooter?: boolean;
  content?: ReactNode;
  cancelText?: string;
  okSeconds?: number;
  cancelSeconds?: number;
  okText?: string;
  onCancel?: (e?: any) => void;
  onClose?: () => void;
  onOk?: (e?: any) => boolean | void;
  okSa?: string;
  cancelSa?: string;
  className?: string;
  okClassName?: string;
  cancelClassName?: string;
  anime?: string;
  okClickEnable?: boolean;
  cancelClickEnable?: boolean;
  moveEnable?: boolean;
  maskEnable?: boolean;
  maskOpacity?: number;
  maskStyle?: any;
  animateEnable?: boolean;
  checkTipEnable?: boolean;
  checkTipText?: string;
  checkTipDefaultStatus?: boolean;
  isH5Popup?: boolean;
  onAfterRender?: () => void;
  style?: React.CSSProperties;
  bodyStyle?: object;
  width?: number | string;
  children?: ReactNode;
  event?: string;
  event_type?: string;
  event_desc?: string;
};

type iFrameProps = {
  title: string;
  url: string;
  targetName: string;
  width: number;
  height: number;
  transparent: boolean;
  onAfterRender: Function;
};

type AlertFunc = (
  title: string | IProps,
  content?: ReactNode,
  onOk?: Function,
  okText?: string,
) => Function | null;
type ConfirmFunc = (
  title: string | IProps,
  content?: ReactNode,
  onOk?: Function | null,
  onCancel?: Function | null,
  okText?: string,
  cancelText?: string,
) => Function | null;
type IframeFunc = (iFrameProps) => void;
// interface IModal extends React.FC<IProps> {
//   alert?:
//   confirm?: (title: string | IProps, content?: JSX.Element, onOk?: Function, onCancel?: Function | null, okText?: string, cancelText?: string) => Function | null
//   iframe?: (iFameProps) => void
// }

const ModalCom: (props: IProps) => JSX.Element | null = observer((props) => {
  const { position, isDark } = useGlobalStore();
  const {
    id = 'modal',
    // 标题
    title = '',
    // 不可关闭，强制的
    closeEnable = true,
    // 是否显示取消按钮
    cancelEnable = true,
    // 是否可以点击遮罩关闭
    maskCloseEnable = true,
    // 是否显示底部
    isShowFooter = true,
    // 是否隐藏标题栏下边框
    isHeaderNoBorder = false,
    // 内容
    content = '',
    // 取消的文本
    cancelText = '取消',
    // 确定的文本
    okText = '确定',
    // 确定倒计时
    okSeconds = -1,
    // 取消倒计时
    cancelSeconds = -1,
    onCancel = () => {},
    onClose = () => {},
    onOk = () => {},
    okSa = '',
    okClickEnable = true,
    cancelClickEnable = true,
    moveEnable = false,
    maskEnable = true,
    maskOpacity = 0.5,
    maskStyle = {},
    animateEnable = true,
    checkTipEnable = false,
    checkTipText = '不在提示',
    checkTipDefaultStatus = true,
    isH5Popup = false,
    cancelSa = '',
    className = '',
    okClassName = 'btn btn-primary btn-lg',
    cancelClassName = isDark
      ? 'btn btn-secondary btn-lg'
      : 'btn btn-outline-default btn-lg',
    anime = 'slide-down',
    children,
    style = {},
    bodyStyle = {},
    onAfterRender = (props) => {},
    width = '',
    event = '',
    event_type = '',
    event_desc = '',
  } = props;

  const visible = useBoolean(true);
  const checkTipStatus = useBoolean(checkTipDefaultStatus);
  const animation = useStateful(anime);
  const zIndex = useStateful(0);
  const okSecondsCounter = useNumber(okSeconds);
  const cancelSecondsCounter = useNumber(cancelSeconds);
  useEffect(() => {
    zIndex.setValue(getZIndex());
  }, []);
  const _track = (type, desc) => {
    if (event && event_type) {
      track.send({
        event,
        event_type: `${event_type}_${type}`,
        desc: event_desc + desc,
      });
    }
  };
  const _onClose = () => {
    // let {onClose} = this.props
    if (closeEnable) {
      // _close();
      const { modal } = globalStore;
      modal.remove(id);
      onClose();
    }
  };

  hooks.useInterval(
    () => {
      okSecondsCounter.decrease();
      if (okSecondsCounter.value === 0) {
        _onOk({ timerEnd: true });
      }
    },
    okSecondsCounter.value > -1 ? 1000 : null,
  );
  hooks.useInterval(
    () => {
      cancelSecondsCounter.decrease();
      if (cancelSecondsCounter.value === 0) {
        _onCancel({ timerEnd: true });
      }
    },
    cancelSecondsCounter.value > -1 ? 1000 : null,
  );

  const _onCancel = (e: any = {}) => {
    _track(
      e.timerEnd ? 'timer_cancel' : 'cancel',
      e.timerEnd ? `超时${cancelText}` : `点击${cancelText}`,
    );

    e.isCheckTip = checkTipStatus.value;

    props.onCancel && props.onCancel(e);
    _onClose();
  };
  const _onOk = async (e: any = {}) => {
    if (!okClickEnable) {
      return;
    }

    _track(
      e.timerEnd ? 'timer_ok' : 'ok',
      e.timerEnd ? `超时${okText}` : `点击${okText}`,
    );

    e.isCheckTip = checkTipStatus.value;

    if (props.onOk) {
      const res = await props.onOk(e);
      if (res === false) {
        return;
      }
    }

    _onClose();
  };

  const _close = () => {
    visible.setFalse();
  };

  useEffect(() => {
    _track('show', '显示');
    onAfterRender(props);
  }, []);

  const renderFooter = () => {
    if (!isShowFooter) {
      return false;
    }
    // 两个按钮都展示
    if (cancelEnable) {
      return (
        <Footer className="ui-modal-footer">
          <ModalButton
            className={cancelClassName}
            data-sa={cancelSa}
            type="default"
            outline={true}
            onClick={_onCancel}
          >
            {cancelText}
            {cancelSeconds > 0 ? `（${cancelSecondsCounter.value}s）` : ''}
          </ModalButton>
          <ModalButton
            className={`${okClassName} ${okClickEnable ? '' : 'disabled'}`}
            data-sa={okSa}
            type="primary"
            onClick={_onOk}
          >
            {okText}
            {okSeconds > 0 ? `（${okSecondsCounter.value}s）` : ''}
          </ModalButton>
        </Footer>
      );
    }
    // 只显示取消按钮

    return (
      <Footer className="ui-modal-footer">
        <ModalButton
          className={`${okClassName} ${okClickEnable ? '' : 'disabled'}`}
          data-sa={okSa}
          type="primary"
          onClick={_onOk}
        >
          {okText}
          {okSeconds > 0 ? `（${okSecondsCounter.value}s）` : ''}
        </ModalButton>
      </Footer>
    );
  };

  const handleCheckTip = () => {
    checkTipStatus.toggle();
  };

  const renderCheckTip = () => {
    if (checkTipEnable) {
      return (
        <div className="check-tip">
          <span onClick={handleCheckTip}>
            <i
              className={`iconfont ${
                checkTipStatus.value ? 'icon-square-check-on' : 'icon-square'
              }`}
            ></i>
            <span>{checkTipText}</span>
          </span>
        </div>
      );
    }
    return null;
  };

  const renderBody = () => {
    if (content && typeof content === 'string') {
      return (
        <Body className="ui-modal-body">
          <div dangerouslySetInnerHTML={{ __html: content }}></div>
          {renderCheckTip()}
        </Body>
      );
    }
    return (
      <Body className="ui-modal-body" style={bodyStyle}>
        {children || content}
        {renderCheckTip()}
      </Body>
    );
  };

  const onMaskClick = (e) => {
    if (maskCloseEnable) {
      _onCancel(e);
    }
  };

  const isMoving = useBoolean(false);
  const ref = useRef<HTMLDivElement>();
  const positionRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const lastPositionRef = useRef<{
    modalX: number;
    modalY: number;
  }>({
    modalX: 0,
    modalY: 0,
  });
  const modalPosition = useStateful<{
    left?: number;
    top?: number;
    transform?: string;
    margin?: string;
  }>({});
  const startPosition = useStateful<{
    modalX: number;
    modalY: number;
    mouseX: number;
    mouseY: number;
  }>({
    modalX: 0,
    modalY: 0,
    mouseX: 0,
    mouseY: 0,
  });

  // let {x:mouseX,mouseY} = position.position

  useEffect(() => {
    if (isMoving.value) {
      const { modalX, modalY, mouseX, mouseY } = startPosition.value;
      const { x: ex, y: ey } = position.position;
      lastPositionRef.current = {
        modalX: Math.floor(ex - mouseX + modalX),
        modalY: Math.floor(ey - mouseY + modalY),
      };
      const { x, y } = positionRef.current!;
      modalPosition.setValue({
        // left: 0,
        // top: 0,
        margin: `${y}px 0 0 ${x}px`,
        transform: `translate3d(${lastPositionRef.current.modalX}px,${lastPositionRef.current.modalY}px,0)`,
      });
    }
  }, [position.position, isMoving.value]);

  const onMouseDown = (e) => {
    if (moveEnable) {
      const { x, y, width, height } = ref.current!.getBoundingClientRect();

      let modalX = 0;
      let modalY = 0;
      if (lastPositionRef.current) {
        modalX = lastPositionRef.current.modalX;
        modalY = lastPositionRef.current.modalY;
      }
      if (!positionRef.current) {
        // modalX = -width / 2;
        // modalY = -height / 2;
        positionRef.current = {
          x: -width / 2,
          y: -height / 2,
        };
      }
      startPosition.setValue({
        modalX,
        modalY,
        mouseX: e.pageX,
        mouseY: e.pageY,
      });
      isMoving.setTrue();
    }
  };

  const onMouseUp = () => {
    if (moveEnable) {
      isMoving.setFalse();
    }
  };
  hooks.useClickAway(ref, onMouseUp);

  if (!visible.value) {
    return null;
  }

  if (width) {
    style.width = width;
  }

  return (
    <>
      {maskEnable && (
        <Mask
          onClick={onMaskClick}
          style={{
            zIndex: zIndex.value,
            background: `rgba(0,0,0,${maskOpacity})`,
            ...maskStyle,
          }}
          className="ui-modal-mask fadeIn animated speed-400"
        />
      )}

      <Wrap
        className={`${className} ${isShowFooter ? '' : 'hideFooter'} ${
          isH5Popup ? '_h5_modal' : ''
        }`}
        style={{
          ...style,
          zIndex: zIndex.value + 1,
          ...modalPosition.value,
          userSelect: isMoving.value ? 'none' : 'auto',
        }}
        ref={ref}
      >
        <div
          className={`inner ${
            animateEnable ? 'fadeInDown animated speed-400 ' : ''
          }`}
        >
          {title && (
            <Header
              className={`ui-modal-header ${
                isHeaderNoBorder ? 'noBorder' : ''
              }`}
              moveEnable={moveEnable}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
            >
              <span className="modal-title">{title}</span>
              {closeEnable && (
                <span onClick={_onCancel} className="modal-close">
                  <Icon type="close"></Icon>
                </span>
              )}
            </Header>
          )}
          {renderBody()}
          {renderFooter()}
        </div>
      </Wrap>
    </>
  );
});

const alert: AlertFunc = (title, content, onOk, okText) => {
  let opts = {};
  opts =
    isPlainObject(title) && !content
      ? assign(opts, title)
      : assign(
          opts,
          pickBy(
            {
              title,
              content,
              onOk,
              okText,
              cancelEnable: false,
            },
            (opt) => {
              return typeof opt !== 'undefined';
            },
          ),
        );

  return renderCom(opts);
};
const confirm: ConfirmFunc = (
  title,
  content,
  onOk,
  onCancel,
  okText,
  cancelText,
) => {
  let opts = {};
  opts =
    isPlainObject(title) && !content
      ? assign(opts, title)
      : assign(
          opts,
          pickBy(
            {
              title,
              content,
              okText,
              cancelText,
              onOk,
              onCancel,
            },
            (opt) => {
              return typeof opt !== 'undefined';
            },
          ),
        );

  return renderCom(opts);
};

const iframe: IframeFunc = ({
  title,
  url,
  targetName = 'child-h5',
  width,
  height = '600px',
  iframeStyle,
  transparent = true,
  isH5Popup = false,
  ...rest
}) => {
  const iframeId = `iframe-${targetName}`;
  if (isH5Popup) {
    url = getUrl(`${url}${url.indexOf('?') > 0 ? '&' : '?'}hide_back=1`, 'h5');
  }
  const opts = {
    title,
    className: `modal-iframe ${transparent && !title ? 'modal-blank' : ''}`,
    style: { width, height: height + 60 },
    content: (
      <iframe
        id={iframeId}
        src={url}
        title="么么直播"
        style={{ height, width: '100%', ...iframeStyle }}
        frameBorder="0"
      ></iframe>
    ),
    maskEnable: true,
    moveEnable: true,
    isShowFooter: false,
    fullBody: true,
    isH5Popup,
    onAfterRender: (props) => {
      if (targetName) {
        const iframe = document.getElementById(iframeId);
        messenger.addTarget(
          (iframe as HTMLIFrameElement).contentWindow,
          targetName,
          () => {
            props.onClose();
          },
        );
      }
    },
    onClose: () => {
      messenger.removeTarget(targetName);
    },
    ...rest,
  };

  return renderCom(opts);
};

const alertContent = (content) => {
  return alert({
    title: '',
    content,
    className: 'modal-blank _content_modal',
    isShowFooter: false,
  });
};

// Modal.proptypes = {
//   id: PropTypes.string,
//   title: PropTypes.string,
//   content: PropTypes.element,
//   btnCloseEnable: PropTypes.bool,
//   cancelEnable: PropTypes.bool,
//   maskCloseEnable: PropTypes.bool,
//   onCancel: PropTypes.func,
//   onClose: PropTypes.func,
//   onOk: PropTypes.func,
//   okSa: PropTypes.string,
//   cancelSa: PropTypes.string,
// }

// const ModalWrap = hoc.withDefaultProps({
//   id: 'modal',
//   title: '',
//   btnCloseEnable: true,
//   cancelEnable: true,
//   maskCloseEnable: true,
//   isShowFooter: true,
//   content: '',
//   cancelText: '取消',
//   okText: '确定',
//   onCancel: (e) => {
//   },
//   onClose: () => {
//   },
//   onOk: (e) => {
//   },
//   okSa: '',
//   cancelSa: '',
//   className: '',
//   okClassName: "btn btn-second",
//   cancelClassName: "btn btn-default"
// }, Modal)

// Modal.close = () => {
//   _close && _close();
// }

const Modal = Object.assign(ModalCom, {
  alert,
  confirm,
  iframe,
  alertContent,
});

const renderCom = (options) => {
  // Modal.close();

  return hoc.render(options, Modal);
};

export const UiModalContent = (props) => {
  const { key, ...restProps } = props;
  return (
    <Modal
      title=""
      isShowFooter={false}
      className="modal-blank _content_modal"
      {...restProps}
    ></Modal>
  );
};

export const ShowUiModal = (props) => {
  const { component, ...options } = props;
  useEffect(() => {
    return hoc.render(options, component);
  }, []);
  return null;
};

export default Modal;
